const LISTING_FILTERS_KEY = Symbol('listingFilters')

interface SelectedFilters {
    manufacturer: Set<string>
    properties: Set<string>
    'min-price': string | number | undefined
    'max-price': string | number | undefined
    rating: string | number | undefined
    'shipping-free': boolean | undefined
    [key: string]: Set<string> | string | number | boolean | undefined
}

export function provideListingFilters() {
    const route = useRoute()
    const { public: { isNewPropertyId: IS_NEW_PROPERTY_ID } } = useRuntimeConfig()

    const selectedFilters = reactive<SelectedFilters>({
        manufacturer: new Set(),
        properties: new Set(),
        'min-price': undefined,
        'max-price': undefined,
        rating: undefined,
        'shipping-free': undefined,
    })

    // Initialize from URL
    for (const param in route.query) {
        if (!(param in selectedFilters)) continue
        if (selectedFilters[param] instanceof Set) {
            for (const el of (route.query[param] as string).split('|').filter(Boolean)) {
                (selectedFilters[param] as Set<string>).add(el)
            }
        } else {
            const v = route.query[param]
            if (v && !Array.isArray(v)) selectedFilters[param] = v
        }
    }
    if (route.query.isNew === '1') selectedFilters.properties.add(IS_NEW_PROPERTY_ID)

    // Keep in sync when URL changes externally
    watch(() => route.query, (q) => {
        selectedFilters.manufacturer.clear()
        for (const id of (q.manufacturer as string ?? '').split('|').filter(Boolean))
            selectedFilters.manufacturer.add(id)
        selectedFilters.properties.clear()
        for (const id of (q.properties as string ?? '').split('|').filter(Boolean))
            selectedFilters.properties.add(id)
        if (q.isNew === '1') selectedFilters.properties.add(IS_NEW_PROPERTY_ID)
        selectedFilters['min-price'] = q['min-price'] as string | undefined
        selectedFilters['max-price'] = q['max-price'] as string | undefined
        selectedFilters.rating = q.rating as string | undefined
        selectedFilters['shipping-free'] = q['shipping-free'] ? true : undefined
    }, { deep: true })

    provide(LISTING_FILTERS_KEY, selectedFilters)
    return selectedFilters
}

export function useListingFilters(): SelectedFilters {
    const filters = inject<SelectedFilters | undefined>(LISTING_FILTERS_KEY, undefined)
    if (filters) return filters
    // No parent provider (e.g. category page CMS siblings) — self-bootstrap.
    // URL watcher inside keeps state in sync regardless.
    return provideListingFilters()
}